// react
import { useContext, useState } from 'react';

// types
import {
	Account,
	Category,
	Component,
	Contract,
	Document,
	Entity,
	ExpenseType,
	Frame,
	Payment,
	Transaction,
	TransactionAugmented,
} from '../../../@types';

// i18n
import { useTranslation } from 'react-i18next';

// router
import { useSearchParams } from 'react-router-dom';

// context
import { ContextUserProjectPermissions } from '../../pages/Project/Project.context';

// hooks
import { useFetch } from '../../../hooks/useFetch';

// mui
import { Box, Chip, Grid, Paper, Tooltip, Typography } from '@mui/material';

// ui
import { Amount } from '../../ui/Amount/Amount';
import { BtnExpand } from '../../ui/BtnExpand/BtnExpand';
import { BtnMenu } from '../../ui/BtnMenu/BtnMenu';
import { ChipStatusTraceability } from '../../ui/ChipStatusTraceability/ChipStatusTraceability';
import { UserAvatarModified } from '../../ui/UserAvatarModified/UserAvatarModified';
import { IconCircleProps } from '../../ui/IconCircle/IconCircle';
import { IconCircleTransaction } from '../../ui/IconCircleTransaction/IconCircleTransaction';
import { IconList, IconListProps } from '../../ui/IconList/IconList';
import { MenuItemProps } from '../../ui/Menu/Menu';
import { RowKeyValue } from '../../ui/RowKeyValue/RowKeyValue';
import { Time } from '../../ui/Time/Time';
import { TypographyCaption } from '../../ui/TypographyCaption/TypographyCaption';

// tabs
import { TabsTransaction } from '../../tabs/TabsTransaction/TabsTransaction';

// dialogs
import { DialogAddComment } from '../../dialogs/DialogAddComment/DialogAddComment';
import { DialogEditTransaction } from '../../dialogs/DialogEditTransaction/DialogEditTransaction';
import { DialogAddRefund } from '../../dialogs/DialogAddRefund/DialogAddRefund';
import { DialogRetryTransaction } from '../../dialogs/DialogRetryTransaction/DialogRetryTransaction';
import { DialogUpdateTransactionStatus } from '../../dialogs/DialogUpdateTransactionStatus/DialogUpdateTransactionStatus';
import { DialogUploadDocsTransaction } from '../../dialogs/DialogUploadDocsTransaction/DialogUploadDocsTransaction';

// links
import { LinkAccount } from '../../links/LinkAccount/LinkAccount';
import { LinkContract } from '../../links/LinkContract/LinkContract';
import { LinkEntity } from '../../links/LinkEntity/LinkEntity';
import { LinkInvoice } from '../../links/LinkInvoice/LinkInvoice';
import { LinkTransaction } from '../../links/LinkTransaction/LinkTransaction';

// icons
import {
	AddCommentOutlined as IconAddComment,
	InsertDriveFileOutlined as IconInsertDriveFile,
	ReplayOutlined as IconReplay,
	RestoreOutlined as IconRestore,
	ModeEditOutlined as IconModeEdit,
	UndoOutlined as IconUndo,
} from '@mui/icons-material';

// theme
import { theme } from '../../../theme';

// utils
import { enhanceComments, enhanceDocuments, hasFailedPayment } from '../../../utils/transactions';

// config
import { featureFlags } from '../../../config/featureFlags';

// props
export interface CardTransactionProps {
	frame?: Frame;
	transaction: TransactionAugmented;
	refetchTransactions?: () => void;
}

export const CardTransaction = ({
	frame,
	transaction,
	refetchTransactions,
}: CardTransactionProps) => {
	// session
	const accessToken = sessionStorage.getItem('accessToken');
	const idToken = sessionStorage.getItem('idToken');

	// params
	const [searchParams, setSearchParams] = useSearchParams();
	const paramDialog = searchParams.get('dialog');
	const paramDialogTransaction = searchParams.get('dialogTransaction');

	// context
	const { userProjectPermissions } = useContext(ContextUserProjectPermissions);

	// hooks
	const { t } = useTranslation();

	// state
	const [documents, setDocuments] = useState<Document[]>([]);
	const [isExpanded, setIsExpanded] = useState(false);

	// handlers
	const handleToggleCard = () => {
		setIsExpanded(!isExpanded);
	};

	const handleCloseDialog = () => {
		searchParams.delete('dialog');
		searchParams.delete('dialogTransaction');
		setSearchParams(searchParams);
	};

	// vars
	const isCashBenefit = transaction.type === 'CASH_BENEFIT';
	const isDirect = transaction.type === 'DIRECT';
	const isOpEx = transaction.type === 'OPEX';
	const isPayment = transaction.type === 'PAYMENT';
	const isPaymentRefund = transaction.type === 'PAYMENT_REFUND';
	const isReverted = transaction.status === 'REVERTED';
	const isSpecial = transaction.type === 'SPECIAL';

	const isFailed = hasFailedPayment(transaction);

	// fetch documents
	useFetch({
		isEnabled: Boolean(transaction),
		url: `${process.env.REACT_APP_API_URL}/documents?transaction=${transaction?.id}&limit=10&page=1&fields=created,notarizationHash,id,name,type,user`,
		options: {
			headers: {
				Authorization: `Bearer ${accessToken}`,
				User: String(idToken),
			},
		},
		onSuccess: (res) => {
			if (res.data) {
				const documentsEnhanced = enhanceDocuments({ documents: res.data });
				setDocuments(documentsEnhanced);
			}
		},
	});

	const payments: Payment[] = [];
	const propsMenuItemEdit: Partial<MenuItemProps> = {
		children: t('editPayment'),
	};
	const propsMenuItemRevert: Partial<MenuItemProps> = {
		children: t('revertPayment'),
	};

	if (transaction?.type === 'CASH_BENEFIT') {
		propsMenuItemEdit.children = t('editCashBenefit');
		propsMenuItemRevert.children = t('revertCashBenefit');
	} else if (transaction?.type === 'DIRECT') {
		propsMenuItemEdit.children = t('editDirectPayment');
	} else if (transaction?.type === 'OPEX') {
		propsMenuItemEdit.children = t('editOpEx');
		propsMenuItemRevert.children = t('revertOpEx');
	} else if (transaction?.type === 'SPECIAL') {
		propsMenuItemEdit.children = t('editSpecialCommitment');
	}

	// add transaction to each payment
	for (let p = 0; p < transaction.payments.length; p++) {
		payments.push({
			...transaction.payments[p],
			transaction,
		});
	}

	const transactionFromAccount = transaction.fromAccount as Account;
	const transactionFromEntity = transaction.fromEntity as Entity;
	const transactionToEntity = transaction.toEntity as Entity;

	const btnMenuItems: MenuItemProps[] = [];

	const hasRefund = Boolean(transaction.refunds && transaction.refunds.length);

	const hasRevert =
		!isReverted &&
		!hasRefund &&
		((transaction.type === 'CASH_BENEFIT' && userProjectPermissions?.cashBenefit?.update) ||
			(transaction.type === 'PAYMENT' && userProjectPermissions?.payment?.update) ||
			(transaction.type === 'OPEX' && userProjectPermissions?.opEx?.update));

	if (userProjectPermissions?.document?.create) {
		btnMenuItems.push({
			children: t('uploadDocuments'),
			icon: <IconInsertDriveFile />,
			onClick: () => {
				searchParams.set('dialog', 'uploadDocsTransaction');
				searchParams.set('dialogTransaction', String(transaction.id));
				setSearchParams(searchParams);
			},
		});
	}

	if (userProjectPermissions?.comment?.create) {
		btnMenuItems.push({
			children: t('addComment'),
			icon: <IconAddComment />,
			onClick: () => {
				searchParams.set('dialog', 'addComment');
				searchParams.set('dialogTransaction', String(transaction.id));
				setSearchParams(searchParams);
			},
		});
	}

	if (
		(transaction.type === 'CASH_BENEFIT' && userProjectPermissions?.cashBenefit?.update) ||
		(transaction.type === 'OPEX' && userProjectPermissions?.opEx?.update) ||
		(transaction.type === 'SPECIAL' && userProjectPermissions?.payment?.update) ||
		(['DIRECT', 'PAYMENT'].includes(transaction.type) && userProjectPermissions?.payment?.update)
	) {
		btnMenuItems.push({
			icon: <IconModeEdit />,
			onClick: () => {
				searchParams.set('dialog', 'editTransaction');
				searchParams.set('dialogTransaction', String(transaction.id));
				setSearchParams(searchParams);
			},
			...propsMenuItemEdit,
		});
	}

	if (isPayment && !isReverted && userProjectPermissions?.payment?.create) {
		btnMenuItems.push({
			children: t('recordRefund'),
			icon: <IconUndo />,
			onClick: () => {
				searchParams.set('dialog', 'recordRefund');
				searchParams.set('dialogTransaction', String(transaction.id));
				setSearchParams(searchParams);
			},
		});
	}

	// Based on testing API v1/transactions/{id}/retry, seems instead of retrying the same transaction,
	// it creates a new one with the same details.
	// So, to avoid confusion, we will not show the retry option for now until we have more clarity on the use case
	// and expected behavior of this endpoint.

	// if (isOpEx && isFailed && !isReverted && userProjectPermissions?.opEx?.update) {
	// 	btnMenuItems.push({
	// 		children: t('retryOpEx'),
	// 		icon: <IconReplay />,
	// 		onClick: () => {
	// 			searchParams.set('dialog', 'retryTransaction');
	// 			searchParams.set('dialogTransaction', String(transaction.id));
	// 			setSearchParams(searchParams);
	// 		},
	// 	});
	// }

	if (hasRevert) {
		btnMenuItems.push({
			icon: <IconRestore color="error" />,
			onClick: () => {
				searchParams.set('dialog', 'revertTransaction');
				searchParams.set('dialogTransaction', String(transaction.id));
				setSearchParams(searchParams);
			},
			...propsMenuItemRevert,
		});
	}

	const { invoiceNumber, invoiceAmount, invoiceCurrency, vendor } = transaction.metadata || {};

	const iconCircleProps: IconCircleProps = {};

	const comments = enhanceComments({ comments: transaction.comments });

	// filter new comments
	const commentsNew = comments.filter((c) => c.isNew);

	// filter new documents
	const documentsNew = documents.filter((d) => d.isNew);

	if (documentsNew.length || commentsNew.length) {
		iconCircleProps.badges = [
			{
				badgeContent: documentsNew.length || commentsNew.length,
				color: 'info',
				component: 'div',
				overlap: 'circular',
				TooltipProps: documentsNew.length
					? { title: t('newDocuments') }
					: { title: t('newComments') },
			},
		];
	}

	const rowAmountFromAccount = (
		<RowKeyValue
			label={t('amount')}
			value={
				<Grid
					alignItems="center"
					container
					justifyContent={{ xs: 'flex-end', lg: 'flex-start' }}
					spacing={1}>
					<Grid item xs="auto">
						<Amount
							conversions={transaction.conversions || []}
							currency={transaction.currency}
							TypographyProps={{ fontWeight: 500 }}
							value={transaction.total - (transaction.totalRefunded || 0)}
						/>
					</Grid>
					{transaction.status === 'REVERTED' && (
						<Grid item xs="auto">
							<Tooltip arrow={true} placement="top" title={t('reverted')}>
								<IconRestore color="error" />
							</Tooltip>
						</Grid>
					)}
				</Grid>
			}
		/>
	);

	const rowFromAccount = (
		<RowKeyValue
			label={t('account')}
			value={
				<Box>
					<LinkAccount account={transactionFromAccount} />
					{transactionFromAccount?.currency && (
						<Typography color="brand.grey.500" component="div" variant="caption">
							{transactionFromAccount.currency}
						</Typography>
					)}
				</Box>
			}
		/>
	);

	const rowDate = (
		<Box
			sx={{
				margin: theme.spacing(0, 0, 2, 0),
				[theme.breakpoints.up('lg')]: {
					margin: 0,
				},
			}}>
			<Grid alignItems="center" container spacing={2} wrap="nowrap">
				<Grid item>
					<IconCircleTransaction transaction={transaction} {...iconCircleProps} />
				</Grid>
				<Grid item>
					<Grid container direction="column">
						<Grid item>
							<Grid alignItems="center" container direction="row" spacing={0.5} wrap="nowrap">
								<Grid item>
									<TypographyCaption id={transaction.id} />
								</Grid>
								{transaction.version && transaction.version !== '1' && (
									<Grid item>
										<Tooltip title={`Fundschain V${transaction.version}`}>
											<Chip
												label={`V${transaction.version}`}
												size="small"
												color="secondary"
												variant="outlined"
												sx={{ height: 18, fontSize: '0.65rem', fontWeight: 600 }}
											/>
										</Tooltip>
									</Grid>
								)}
							</Grid>
						</Grid>
						<Grid item>
							<Time date={transaction.date} />
						</Grid>
					</Grid>
				</Grid>
			</Grid>
		</Box>
	);

	const listExpenseType: IconListProps['list'] = [];

	if (transaction.component) {
		listExpenseType.push({
			IconCircleProps: {
				TooltipProps: {
					title: t('component'),
				},
				type: 'component',
			},
			value: (
				<Box
					sx={{
						overflow: 'hidden',
						textOverflow: 'ellipsis',
						whiteSpace: {
							lg: 'nowrap',
						},
					}}>
					{(transaction.component as Component).name}
				</Box>
			),
		});
	}

	if (transaction.category) {
		listExpenseType.push({
			IconCircleProps: {
				TooltipProps: {
					title: t('category'),
				},
				type: 'category',
			},
			value: (
				<Box
					sx={{
						overflow: 'hidden',
						textOverflow: 'ellipsis',
						whiteSpace: {
							lg: 'nowrap',
						},
					}}>
					{(transaction.category as Category).name}
				</Box>
			),
		});
	}

	if (transaction.expenseType) {
		listExpenseType.push({
			IconCircleProps: {
				TooltipProps: {
					title: t('expenseType'),
				},
				type: 'expenseType',
			},
			value: (
				<Box
					sx={{
						overflow: 'hidden',
						textOverflow: 'ellipsis',
						whiteSpace: {
							lg: 'nowrap',
						},
					}}>
					{(transaction.expenseType as ExpenseType).name}
				</Box>
			),
		});
	}

	if (!listExpenseType.length) {
		listExpenseType.push({
			IconCircleProps: {
				type: 'priority',
			},
			value: t('na'),
		});
	}

	const rowClassification = (
		<RowKeyValue
			label={t('classification')}
			value={
				<Grid container>
					<Grid item xs={12}>
						<IconList
							GridItemProps={{
								xs: 12,
							}}
							GridItemIconProps={{
								display: {
									xs: 'none',
									lg: 'inline-flex',
								},
							}}
							GridItemValueProps={{
								sx: {
									overflow: 'hidden',
									textOverflow: 'ellipsis',
									whiteSpace: {
										lg: 'nowrap',
									},
								},
							}}
							list={listExpenseType}
							sxGrid={{
								justifyContent: {
									xs: 'flex-end',
									lg: 'flex-start',
								},
							}}
						/>
					</Grid>
				</Grid>
			}
		/>
	);

	const rowFromEntity = (
		<RowKeyValue
			label={t('entity')}
			value={<LinkEntity entity={transactionFromEntity} hasEllipsis={true} />}
		/>
	);

	const listIcons: IconListProps['list'] = [
		{
			IconCircleProps: {
				TooltipProps: {
					title: t('documents'),
				},
				type: 'document',
			},
			value: documents.length,
		},
		{
			IconCircleProps: {
				TooltipProps: {
					title: t('comments'),
				},
				type: 'comment',
			},
			value: transaction.comments.length,
		},
	];

	if (featureFlags['IFRS']) {
		listIcons.push({
			IconCircleProps: {
				TooltipProps: {
					title: t('reports'),
				},
				type: 'ifr',
			},
			value: transaction.ifrs?.length || 0,
		});
	}

	const rowIconList = (
		<Grid container justifyContent={{ xs: 'flex-end', lg: 'flex-start' }}>
			<Grid item>
				<IconList list={listIcons} />
			</Grid>
		</Grid>
	);

	const rowStatusTraceability = (
		<RowKeyValue
			label={t('evidenced')}
			value={
				<ChipStatusTraceability
					status={transaction.status}
					traceability={transaction.traceability}
				/>
			}
		/>
	);

	const rowModified = (
		<RowKeyValue
			label={t('updated')}
			value={
				<UserAvatarModified
					transaction={transaction}
					activityTypes={['TRANSACTION_UPDATED', 'TRANSACTION_CREATED']}
				/>
			}
		/>
	);

	const renderRow = () => {
		if (isCashBenefit) {
			return (
				<>
					<Grid item xs={12} lg={frame === 'BENEFICIARY' ? 2 : 1.5}>
						{rowDate}
					</Grid>
					<Grid item xs={12} lg={frame === 'BENEFICIARY' ? 2 : 1.5}>
						{rowClassification}
					</Grid>
					{frame !== 'ENTITY' && (
						<Grid item xs={12} lg={1.5}>
							{rowFromEntity}
						</Grid>
					)}
					{frame !== 'BENEFICIARY' && (
						<Grid item xs={12} lg={1.5}>
							<RowKeyValue
								label={t('beneficiary')}
								value={<LinkEntity entity={transactionToEntity} hasEllipsis={true} />}
							/>
						</Grid>
					)}
					<Grid item xs={12} lg={1}>
						{rowStatusTraceability}
					</Grid>
					<Grid item xs={12} lg={1}>
						{rowAmountFromAccount}
					</Grid>
					<Grid item xs={12} lg={1}>
						{rowFromAccount}
					</Grid>
					<Grid item xs={12} lg={1.5}>
						{rowModified}
					</Grid>
					<Grid item xs={12} lg={1}>
						{rowIconList}
					</Grid>
				</>
			);
		}

		if (isOpEx) {
			const rowEntityVendor = (
				<IconList
					GridItemProps={{
						xs: 12,
					}}
					GridItemIconProps={{
						display: {
							xs: 'none',
							lg: 'inline-flex',
						},
					}}
					GridItemValueProps={{
						sx: {
							overflow: 'hidden',
							textOverflow: 'ellipsis',
							whiteSpace: {
								lg: 'nowrap',
							},
						},
					}}
					list={[
						{
							IconCircleProps: {
								TooltipProps: {
									title: t('entity'),
								},
								type: 'entity',
							},
							value: <LinkEntity entity={transactionFromEntity} hasEllipsis={true} />,
						},
						{
							IconCircleProps: {
								TooltipProps: {
									title: t('vendor'),
								},
								type: 'vendor',
							},
							value: <Typography>{vendor || t('na')}</Typography>,
						},
					]}
					sxGrid={{
						justifyContent: {
							xs: 'flex-end',
							lg: 'flex-start',
						},
					}}
				/>
			);

			return (
				<>
					<Grid item xs={12} lg={1.5}>
						{rowDate}
					</Grid>
					<Grid item xs={12} lg={1.5}>
						{rowClassification}
					</Grid>
					<Grid item xs={12} lg={2} flex={1}>
						{rowEntityVendor}
					</Grid>
					<Grid item xs={12} lg={1.5}>
						{rowStatusTraceability}
					</Grid>
					<Grid item xs={12} lg={1}>
						{rowAmountFromAccount}
					</Grid>
					<Grid item xs={12} lg={1}>
						{rowFromAccount}
					</Grid>
					<Grid item xs={12} lg={1.5}>
						{rowModified}
					</Grid>
					<Grid item xs={12} lg={1}>
						{rowIconList}
					</Grid>
				</>
			);
		}

		if (isPaymentRefund) {
			const transactionRefundedObj =
				typeof transaction.transactionRefunded === 'object'
					? (transaction.transactionRefunded as unknown as Transaction)
					: undefined;
			const transactionToAccount = transaction.toAccount as Account;

			const rowTransactionRefunded = (
				<RowKeyValue
					label={t('refunded')}
					value={
						<LinkTransaction
							GridProps={{ width: '100%' }}
							hasIconCircle={false}
							transaction={transactionRefundedObj}
						/>
					}
				/>
			);

			const rowEntities = (
				<RowKeyValue
					label={t('entities')}
					value={
						<Grid container>
							<Grid item xs={12}>
								<IconList
									GridItemProps={{ xs: 12 }}
									GridItemIconProps={{
										display: {
											xs: 'none',
											lg: 'inline-flex',
										},
									}}
									GridItemValueProps={{
										sx: {
											overflow: 'hidden',
											textOverflow: 'ellipsis',
											whiteSpace: {
												lg: 'nowrap',
											},
										},
									}}
									list={[
										{
											IconCircleProps: {
												TooltipProps: { title: t('supplier') },
												type: 'supplier',
											},
											value: <LinkEntity entity={transactionFromEntity} hasEllipsis={true} />,
										},
										{
											IconCircleProps: {
												TooltipProps: { title: t('entity') },
												type: 'entity',
											},
											value: transactionToEntity ? (
												<LinkEntity entity={transactionToEntity} hasEllipsis={true} />
											) : (
												<Typography>{t('na')}</Typography>
											),
										},
									]}
									sxGrid={{
										justifyContent: {
											xs: 'flex-end',
											lg: 'flex-start',
										},
									}}
								/>
							</Grid>
						</Grid>
					}
				/>
			);

			const rowToAccount = (
				<RowKeyValue
					label={t('account')}
					value={
						<Box>
							<LinkAccount account={transactionToAccount} />
							{transactionToAccount?.currency && (
								<Typography color="brand.grey.500" component="div" variant="caption">
									{transactionToAccount.currency}
								</Typography>
							)}
						</Box>
					}
				/>
			);

			return (
				<>
					<Grid item xs={12} lg={2}>
						{rowDate}
					</Grid>
					<Grid item xs={12} lg={1.5}>
						{rowTransactionRefunded}
					</Grid>
					<Grid item xs={12} lg={2.5}>
						{rowEntities}
					</Grid>
					<Grid item xs={12} lg={1}>
						{rowAmountFromAccount}
					</Grid>
					<Grid item xs={12} lg={1.5}>
						{rowToAccount}
					</Grid>
					<Grid item xs={12} lg={1.5}>
						{rowModified}
					</Grid>
					<Grid item xs={12} lg={1}>
						{rowIconList}
					</Grid>
				</>
			);
		}

		if (isDirect || isPayment || isSpecial) {
			let iconCircleTypeContract: IconCircleProps['type'] = 'contract';

			if (transaction.type === 'DIRECT' && !transaction.contract) {
				iconCircleTypeContract = 'priority';
			}

			const rowSupplier = (
				<IconList
					GridItemProps={{
						xs: 12,
					}}
					GridItemIconProps={{
						display: {
							xs: 'none',
							lg: 'inline-flex',
						},
					}}
					GridItemValueProps={{
						sx: {
							overflow: 'hidden',
							textOverflow: 'ellipsis',
							whiteSpace: {
								lg: 'nowrap',
							},
						},
					}}
					list={[
						{
							IconCircleProps: {
								TooltipProps: {
									title: t('supplier'),
								},
								type: 'supplier',
							},
							value: transactionToEntity ? (
								<RowKeyValue
									label={t('supplier')}
									value={<LinkEntity entity={transactionToEntity} hasEllipsis={true} />}
								/>
							) : (
								<Typography>{t('na')}</Typography>
							),
						},
						{
							IconCircleProps: {
								TooltipProps: {
									title: t('contract'),
								},
								type: iconCircleTypeContract,
							},
							value: <LinkContract contract={transaction.contract as Contract} />,
						},
						{
							IconCircleProps: {
								TooltipProps: {
									title: t('invoice'),
								},
								type: 'invoice',
							},
							value: invoiceNumber ? (
								<LinkInvoice invoiceNumber={invoiceNumber} />
							) : (
								<Amount value={invoiceAmount} currency={invoiceCurrency} />
							),
						},
					]}
					sxGrid={{
						justifyContent: {
							xs: 'flex-end',
							lg: 'flex-start',
						},
					}}
				/>
			);

			return (
				<>
					<Grid item xs={12} lg={1.5}>
						{rowDate}
					</Grid>
					<Grid item xs={12} lg={2}>
						{rowClassification}
					</Grid>
					{frame === 'SUPPLIER' ? (
						<Grid item xs={12} lg={1.5}>
							{rowFromEntity}
						</Grid>
					) : (
						<Grid item xs={12} lg={1.5} flex={1}>
							{rowSupplier}
						</Grid>
					)}
					<Grid item xs={12} lg={1.5}>
						{rowStatusTraceability}
					</Grid>
					<Grid item xs={12} lg={1}>
						{rowAmountFromAccount}
					</Grid>
					<Grid item xs={12} lg={1}>
						{rowFromAccount}
					</Grid>
					<Grid item xs={12} lg={1.5}>
						{rowModified}
					</Grid>
					<Grid item xs={12} lg={1}>
						{rowIconList}
					</Grid>
				</>
			);
		}
	};

	return (
		<>
			<Paper
				sx={(theme) => {
					const borderColor = isExpanded ? theme.palette.brand.blue[500] : 'transparent';
					return {
						border: `1px ${borderColor} solid`,
						position: 'relative',
					};
				}}>
				<Box sx={{ padding: theme.spacing(1.5) }}>
					<Grid container alignItems={'center'} spacing={{ xs: 1, lg: 2 }}>
						{renderRow()}
						<Grid item xs={12} lg={true}>
							<Grid alignItems="center" container justifyContent={{ lg: 'flex-end' }} spacing={1}>
								{btnMenuItems.length > 0 && (
									<Grid
										item
										sx={{
											position: 'absolute',
											top: 0,
											right: theme.spacing(1),
											[theme.breakpoints.up('lg')]: {
												position: 'relative',
												top: 'auto',
												right: 'auto',
											},
										}}>
										<BtnMenu items={btnMenuItems} />
									</Grid>
								)}
								<Grid item>
									<BtnExpand
										isExpanded={isExpanded}
										label={isExpanded ? t('hideDetails') : t('seeDetails')}
										onClick={handleToggleCard}
									/>
								</Grid>
							</Grid>
						</Grid>
					</Grid>
				</Box>
				{isExpanded && <TabsTransaction transaction={transaction} />}
			</Paper>
			<DialogAddComment
				isOpen={paramDialog === 'addComment' && paramDialogTransaction === String(transaction.id)}
				onClose={handleCloseDialog}
				onSuccess={() => {
					if (refetchTransactions) {
						refetchTransactions();
					}
				}}
			/>
			<DialogEditTransaction
				isOpen={
					paramDialog === 'editTransaction' && paramDialogTransaction === String(transaction.id)
				}
				onClose={handleCloseDialog}
				onSuccess={() => {
					if (refetchTransactions) {
						refetchTransactions();
					}
				}}
				transaction={transaction}
			/>
			<DialogUploadDocsTransaction
				isOpen={
					paramDialog === 'uploadDocsTransaction' &&
					paramDialogTransaction === String(transaction.id)
				}
				onClose={handleCloseDialog}
				onSuccess={() => {
					if (refetchTransactions) {
						refetchTransactions();
					}
				}}
				transaction={transaction}
			/>
			<DialogUpdateTransactionStatus
				isOpen={
					paramDialog === 'revertTransaction' && paramDialogTransaction === String(transaction.id)
				}
				onClose={handleCloseDialog}
				onSuccess={() => {
					if (refetchTransactions) {
						refetchTransactions();
					}
				}}
				status="REVERTED"
				type={transaction.type}
			/>
			<DialogAddRefund
				isOpen={paramDialog === 'recordRefund' && paramDialogTransaction === String(transaction.id)}
				onClose={handleCloseDialog}
				onSuccess={() => {
					if (refetchTransactions) {
						refetchTransactions();
					}
				}}
			/>
			<DialogRetryTransaction
				isOpen={
					paramDialog === 'retryTransaction' && paramDialogTransaction === String(transaction.id)
				}
				onClose={handleCloseDialog}
				onSuccess={() => {
					if (refetchTransactions) {
						refetchTransactions();
					}
				}}
			/>
		</>
	);
};
